/*
 * Module for the topic response, including the preview and properties submodules
 */

define([
  "marionette",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicResponse/topicResponseView",
  "modules/topicsSplit/topic/topicResponse/topicResponsePreview/topicResponsePreviewModule",
  "modules/topicsSplit/topic/topicResponse/topicResponseProperties/topicResponsePropertiesModule",
  "modules/topicsSplit/topic/topicResponse/topicCategories/topicCategoriesModule",
  "modules/topicsSplit/topic/topicResponse/topicAgentAssist/topicAgentAssistModule",
  "./topicAttributes/topicAttributesModule",
  "./topicCategories/topicCategoriesHeader/topicCategoriesHeaderModule",
], function (
  Marionette,
  Messenger,
  TopicResponseView,
  TopicResponsePreviewModule,
  TopicResponsePropertiesModule,
  TopicCategoriesModule,
  TopicAgentAssistModule,
  TopicAttributesModule,
  TopicCategoriesHeaderModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function ({
      allowDisable,
      region,
      skipExpandWithContext,
      skipShareResponseLinks,
      skipResponseTransform,
      showAgentAssist,
      showAttributesTab,
      showCategoryTab,
    }) {
      this.name = "topicResponse";
      this.region = region;
      this._setUpVents();
      this.skipExpandWithContext = skipExpandWithContext;
      this.skipShareResponseLinks = skipShareResponseLinks;
      this.skipResponseTransform = skipResponseTransform;
      if (allowDisable === undefined) {
        this.allowDisable = true;
      } else {
        this.allowDisable = allowDisable;
      }
      this.showAgentAssist = showAgentAssist !== undefined ? showAgentAssist : true;
      this.showCategoryTab = showCategoryTab !== undefined ? showCategoryTab : true;
      this.showAttributesTab = showAttributesTab !== undefined ? showAttributesTab : true;
    },
    show: function (conversation, liveConversationRoot) {
      this.conversation = conversation;
      conversation.allowAttributes = Messenger.reqres.request("can:view:topicAttributes");
      conversation.allowCategories = Messenger.reqres.request("can:view:topicCategoryCodes");
      conversation.readOnly = Messenger.reqres.request("topic:read:only");
      if (!conversation.allowCategories) {
        this.showCategoryTab = false;
      }
      if (!conversation.allowAttributes) {
        this.showAttributesTab = false;
      }
      this.layout = new TopicResponseView.topicResponseLayout({ model: conversation });
      this.preview = this.addSubModule(
        new TopicResponsePreviewModule({
          region: this.layout.preview,
          allowDisable: this.allowDisable,
          skipExpandWithContext: this.skipExpandWithContext,
          skipShareResponseLinks: this.skipShareResponseLinks,
          skipResponseTransform: this.skipResponseTransform,
        }),
      );
      this.properties = this.addSubModule(
        new TopicResponsePropertiesModule({ region: this.layout.properties }),
      );
      if (this.showCategoryTab) {
        this.categories = this.addSubModule(
          new TopicCategoriesModule({ region: this.layout.categories }),
        );
        this.categoriesHeader = this.addSubModule(
          new TopicCategoriesHeaderModule({ region: this.layout.categoriesHeader }),
        );
      }
      if (this.showAttributesTab) {
        this.attributes = this.addSubModule(
          new TopicAttributesModule({ region: this.layout.topicAttributes }),
        );
      }
      this.agentAssist = this.addSubModule(
        new TopicAgentAssistModule({ region: this.layout.agentAssist }),
      );

      this.layout.on("click:agentAssist", () =>
        this._saveTopicIfNewAndCollections().then(() => this.layout.selectAgentAssistTab()),
      );
      this.layout.on("click:attributes", () =>
        this._saveTopicIfNewAndCollections().then(() => this.layout.selectAttributesTab()),
      );
      this.layout.on("click:categories", () =>
        this._saveTopicIfNewAndCollections().then(() => this.layout.selectCategoriesTab()),
      );
      this.layout.on("click:preview", () =>
        this._saveTopicIfNewAndCollections().then(() => this.layout.selectResponseTab()),
      );
      this.layout.on("show:categories", () => {
        if (this.categories) {
          this.categories.show(this.conversation.categories());
          this.categoriesHeader.show(this.conversation.categories());
        }
      });
      this.layout.on("show:agentAssist", () =>
        this.agentAssist.show(this.conversation.epcMigrations()),
      );
      this.layout.on("show:attributes", () =>
        this.attributes.show(this.conversation.topicAttributes()),
      );
      // Re-show preview to reflect any local changes in categories / attributes
      this.layout.on("show:preview", () =>
        this.preview.show({
          conversation: this.conversation,
          liveConversationRoot: liveConversationRoot,
          canEdit: true,
          readOnly: Messenger.reqres.request("topic:read:only"),
          showCampaigns: Messenger.reqres.request("user:is:admin"),
        }),
      );
      this.layout.on(
        "show",
        function () {
          this._showSubViews(conversation, liveConversationRoot);
        },
        this,
      );
      this.showRegion(this.layout);
    },
    _decideResponseTabs: function () {
      //decide if we show the categories and AgentAssist tabs
      const isLeafNode = this.conversation.decisionCol.length === 0;
      const kbModel = Messenger.reqres.request("active:kb:model");
      kbModel.onSynced(function () {
        if (kbModel.get("agentAssist") && isLeafNode) {
          this.layout.showAgentAssistTab();
          this.showAgentAssist = true;
        } else {
          this.layout.hideAgentAssistTab();
          this.showAgentAssist = false;
        }
      }, this);
      if (this.categories && this.showCategoryTab) {
        const catOptions = Messenger.reqres.request("kb:categories");
        catOptions.onSynced(function () {
          let showCategories = false;
          // validate that there are categories and at least one with category values
          if (catOptions.length > 0) {
            catOptions.each(function (cat) {
              if (cat.getValues().length > 0) {
                showCategories = true;
              }
            });
          }
          if (showCategories && this.showCategoryTab) {
            this.layout.showCategoriesTab();
          } else {
            this.layout.hideCategoriesTab();
          }
        }, this);
      }

      if (this.attributes && this.showAttributesTab) {
        this.layout.showAttributes();
      }
    },
    _saveTopicIfNewAndCollections: function () {
      // Cannot use "topic:saveIfNew" because the Conversation also needs to be saved so that
      // a proper Response ID exists for each of the child elements.
      const promises = [Messenger.savePromise()];
      // All the children require a Response ID, so don't bother trying to save them if
      // there is no Response ID
      if (this.conversation.get("responseId")) {
        // Make sure to only try to save collections that have already been loaded, otherwise
        // we could wipe out existing data.
        if (this.showAgentAssist) {
          const epcMigrations = this.conversation.epcMigrations();
          if (!epcMigrations.isRequested || epcMigrations.isSynced) {
            promises.push(epcMigrations.saveCollectionPromise());
          }
        }
        if (this.categories) {
          const categories = this.conversation.categories();
          if (!categories.isRequested || categories.isSynced) {
            promises.push(categories.saveCollectionPromise());
          }
        }
        if (this.attributes) {
          const attributes = this.conversation.topicAttributes();
          if (!attributes.isRequested || attributes.isSynced) {
            promises.push(attributes.saveCollectionPromise());
          }
        }
      }
      return Promise.all(promises);
    },
    _showSubViews: function (conversation, liveConversationRoot) {
      this._decideResponseTabs();
      this.preview.show({
        conversation: conversation,
        liveConversationRoot: liveConversationRoot,
        canEdit: true,
        readOnly: Messenger.reqres.request("topic:read:only"),
        showCampaigns: Messenger.reqres.request("user:is:admin"),
      });
      this.properties.show(conversation);
    },
    _setUpVents: function () {
      //event when a node from the conversation diagram is selected
      Messenger.vent.on(
        "selected:node",
        function (conversationNode) {
          this.preview.conversationNode = conversationNode; //set the conversation node for the preview
          this.preview.setResponse();
          this.preview.setActionGroup();
          this.preview.setCampaigns();
          this.properties.show(conversationNode);
          this.conversation = conversationNode;
          this._decideResponseTabs();
          //switch to the response tab
          this.layout.selectResponseTab();
        },
        this,
      );

      Messenger.reqres.setHandler(
        "get:response:conversationNode",
        function () {
          return this.conversation;
        },
        this,
      );
    },
  });
});
